import React from 'react';
import {
  FieldValues,
  Path,
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import { FormSchema, FormFieldConfig } from './locationSchemas';
import DynamicField from './DynamicField';

interface DynamicFormProps<T extends FieldValues = FieldValues> {
  schema: FormSchema;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  clearErrors: UseFormClearErrors<T>;
  setError?: UseFormSetError<T>;
  control: Control<T>;
  isLoading?: boolean;
  className?: string;
}

export default function DynamicForm<T extends FieldValues = FieldValues>({
  schema,
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  setError,
  control,
  isLoading = false,
  className = '',
}: DynamicFormProps<T>) {
  const values = watch();

  const visibleFields = schema.fields.filter((field: FormFieldConfig) => {
    if (!field.showWhen) return true;

    const { field: watchField, equals, in: inValues } = field.showWhen;
    if (!watchField) return true;

    const conditionValue = values?.[watchField];

    if (equals !== undefined) {
      if (Array.isArray(conditionValue)) {
        if (!conditionValue.includes(equals)) return false;
      } else {
        if (conditionValue !== equals) return false;
      }
    }

    if (inValues && inValues.length > 0) {
      if (Array.isArray(conditionValue)) {
        const hasMatch = conditionValue.some((val) => inValues.includes(val));
        if (!hasMatch) return false;
      } else {
        if (!inValues.includes(conditionValue)) return false;
      }
    }

    return true;
  });

  const hasGridCols = className.includes('grid-cols-');
  const defaultGridClass = hasGridCols ? '' : 'grid-cols-1 md:grid-cols-2';

  return (
    <div className={`grid gap-4 ${defaultGridClass} ${className}`}>
      {visibleFields.map((field: FormFieldConfig) => (
        <DynamicField
          key={field.name}
          field={field}
          name={field.name as Path<T>}
          className={field.className}
          disabled={isLoading || field.disabled}
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          clearErrors={clearErrors}
          setError={setError}
          control={control}
        />
      ))}
    </div>
  );
}
